import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import CategoryBar from '../components/CategoryBar';

const kpis = [
    { title: 'Inpatient', value: '14', change: '12%', changeType: 'up' },
    { title: 'Avg length of stay', value: '4', unit: 'days', change: '9%', changeType: 'up' },
    { title: 'Occupancy rate', value: '70%', change: '9%', changeType: 'down' },
];

const speciesBars = [
    { label: 'Canine', value: 30 },
    { label: 'Feline', value: 60 },
    { label: 'Others', value: 10 },
];

const inpatientData = [
    { name: 'Jan', value: 12 },
    { name: 'Feb', value: 5 },
    { name: 'Mar', value: 11 },
    { name: 'Apr', value: 20 },
    { name: 'May', value: 4 },
    { name: 'Jun', value: 5 },
    { name: 'Jul', value: 8 },
    { name: 'Aug', value: 9 },
    { name: 'Sep', value: 12 },
    { name: 'Oct', value: 2 },
    { name: 'Nov', value: 14 },
    { name: 'Dec', value: 4 },
];

const dischargePie = [
    { name: 'Discharged', value: 75 },
    { name: 'Active', value: 25 },
];

const currentDischarges = [
    { pet: 'Walter R.', time: '11:00' },
    { pet: 'Oscar B.', time: '12:30' },
    { pet: 'Max .r', time: '3:00' },
];

const PIE_COLORS = ['#0f4c5c', '#f97316'];

const InpatientClinicalManagement = ({ onBack }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
            <ViewHeader title="Inpatient Clinical Management (Monthly)" subtitle="2026" onBack={onBack} />

            {/* Top row — equal height */}
            <div style={{ display: 'flex', gap: '16px' }}>
                <div className="clinic-dashboard-section-card" style={{ flex: 1, animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Key metrics</div>
                    <div className="clinic-dashboard-kpi-row">
                        {kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>

                <div className="clinic-dashboard-section-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Species breakdown</div>
                    <div className="clinic-dashboard-catbar-list large">
                        {speciesBars.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                    </div>
                </div>
            </div>

            {/* Bottom row — line chart left, pie + list side-by-side right */}
            <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>
                <div className="clinic-dashboard-section-card" style={{ flex: 1.2, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Inpatient over time</div>
                    <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '200px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={inpatientData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Line animationBegin={600} type="monotone" dataKey="value" stroke="#0f4c5c" strokeWidth={2} dot={{ r: 4, fill: '#0f4c5c' }} activeDot={{ r: 6 }} name="inpatient" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Right side: discharged pie + current discharges side by side */}
                <div style={{ display: 'flex', gap: '16px', flex: 0.8, minHeight: 0 }}>
                    <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '240ms' }}>
                        <div className="clinic-dashboard-section-title">Discharged</div>
                        <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '220px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <ResponsiveContainer width="100%" height="85%">
                                <PieChart>
                                    <Pie
                                        data={dischargePie}
                                        outerRadius={85}
                                        paddingAngle={2}
                                        dataKey="value"
                                        stroke="#fff"
                                        strokeWidth={2}
                                    >
                                        {dischargePie.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="clinic-dashboard-legend">
                                {dischargePie.map((entry, index) => (
                                    <div key={index} className="clinic-dashboard-legend-item">
                                        <span className="clinic-dashboard-legend-dot" style={{ backgroundColor: PIE_COLORS[index] }} />
                                        <span className="clinic-dashboard-legend-label">{entry.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="clinic-dashboard-section-card" style={{ flex: 1, alignSelf: 'flex-start', animationDelay: '320ms' }}>
                        <div className="clinic-dashboard-section-title">Current discharges</div>
                        <div className="clinic-dashboard-discharge-list">
                            {currentDischarges.map((row, i) => (
                                <div key={i} className="clinic-dashboard-discharge-item">
                                    <span className="clinic-dashboard-discharge-name">{row.pet}</span>
                                    <span className="clinic-dashboard-discharge-time">{row.time}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InpatientClinicalManagement;
